/**
 * Motion presets for Motion React transitions and variants.
 */
import { MOTION_VALUES } from "./motion-constants";

/**
 * Standardized duration values for animations (in seconds).
 */
export const DURATION = MOTION_VALUES.duration;

/**
 * Standardized delay values (in seconds).
 */
export const DELAY = {
	...MOTION_VALUES.delay,
	/**
	 * Calculate staggered delay for sequential animations.
	 * @param index - Item index in sequence.
	 * @param baseDelay - Base delay multiplier in seconds.
	 * @returns Delay in seconds.
	 */
	stagger: (index: number, baseDelay = 0.03) => index * baseDelay,
} as const;

/**
 * Standardized easing curves.
 */
export const EASING = {
	in: "easeIn",
	out: "easeOut",
	inOut: "easeInOut",
} as const;

/**
 * Spring configuration presets for Motion React.
 */
export const SPRING = {
	gentle: { type: "spring" as const, ...MOTION_VALUES.spring.gentle },
	smooth: { type: "spring" as const, ...MOTION_VALUES.spring.smooth },
	snappy: { type: "spring" as const, ...MOTION_VALUES.spring.snappy },
	bouncy: { type: "spring" as const, ...MOTION_VALUES.spring.bouncy },
	responsive: { type: "spring" as const, ...MOTION_VALUES.spring.responsive },
	tight: { type: "spring" as const, ...MOTION_VALUES.spring.tight },
	crisp: { type: "spring" as const, ...MOTION_VALUES.spring.crisp },
	softBounce: { type: "spring" as const, ...MOTION_VALUES.spring.softBounce },
} as const;

/**
 * Standardized scale presets.
 */
export const SCALE = MOTION_VALUES.scale;

/**
 * Standardized opacity presets.
 */
export const OPACITY = MOTION_VALUES.opacity;

/**
 * Transition helpers for common patterns.
 */
export const TRANSITION = {
	/**
	 * Quick fade transition.
	 * @param duration - Transition duration in seconds.
	 * @returns Motion transition config.
	 */
	fade: (duration = DURATION.fast) => ({
		duration,
		ease: EASING.inOut,
	}),
	/**
	 * Smooth slide transition.
	 * @param duration - Transition duration in seconds.
	 * @returns Motion transition config.
	 */
	slide: (duration = DURATION.normal) => ({
		duration,
		ease: EASING.out,
	}),
	/**
	 * Instant transition (no animation).
	 * @returns Motion transition config.
	 */
	instant: () => ({ duration: DURATION.instant }),
} as const;
